
import React from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFormDraft } from "@/hooks/useFormDraft";
import { updateUserProfile } from "@/services/edgeFunctionWrappers/profilesManagementWrapper";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

interface ProfileEmergencyContactProps {
  userId?: string;
  profileData: any;
}

const emergencyContactSchema = z.object({
  emergency_contact_name: z.string().min(1, "Contact name is required"),
  emergency_contact_phone: z.string().min(1, "Contact phone is required"),
  emergency_contact_relation: z.string().optional(),
});

type EmergencyContactValues = z.infer<typeof emergencyContactSchema>;

export const ProfileEmergencyContact: React.FC<ProfileEmergencyContactProps> = ({
  userId,
  profileData
}) => {
  const { t } = useTranslation(["profile", "common"]);
  
  const form = useForm<EmergencyContactValues>({
    resolver: zodResolver(emergencyContactSchema),
    defaultValues: {
      emergency_contact_name: profileData?.emergency_contact_name || "",
      emergency_contact_phone: profileData?.emergency_contact_phone || "",
      emergency_contact_relation: profileData?.emergency_contact_relation || "",
    },
  });

  // Brouillon persisté par utilisateur — restauration silencieuse au montage.
  const { clearDraft } = useFormDraft("profile-emergency", form, {
    entityId: userId ?? null,
    isEmpty: (v) =>
      !v.emergency_contact_name && !v.emergency_contact_phone && !v.emergency_contact_relation,
  });

  const onSubmit = async (data: EmergencyContactValues) => {
    if (!userId) {
      toast.error(t("errors.notAuthenticated", { ns: "profile" }));
      return;
    }

    try {
      // updateUserProfile renvoie un booléen : le déstructurer en { error }
      // donnait toujours `undefined`, donc un succès systématique.
      const ok = await updateUserProfile(userId, data);
      if (ok) {
        clearDraft();
        toast.success(t("success.contactUpdated", { ns: "profile" }));
      } else {
        toast.error(t("errors.updateFailed", { ns: "profile" }));
      }
    } catch (error) {
      console.error("Error updating emergency contact:", error);
      toast.error(t("errors.updateFailed", { ns: "profile" }));
    }
  };

  return (
    <Card className="border shadow-sm">
      <CardHeader>
        <CardTitle>{t("emergency.title", { ns: "profile" })}</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="emergency_contact_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("emergency.name", { ns: "profile" })}</FormLabel>
                    <FormControl>
                      <Input placeholder={t("emergency.namePlaceholder", { ns: "profile" })} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="emergency_contact_phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("emergency.phone", { ns: "profile" })}</FormLabel>
                    <FormControl>
                      <Input placeholder={t("emergency.phonePlaceholder", { ns: "profile" })} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="emergency_contact_relation"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("emergency.relation", { ns: "profile" })}</FormLabel>
                  <FormControl>
                    <Input placeholder={t("emergency.relationPlaceholder", { ns: "profile" })} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end">
              <Button type="submit">{t("save", { ns: "common" })}</Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};
